import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

export interface TicketFilters {
  priority: 'all' | 'high' | 'medium' | 'low';
  assignment: 'all' | 'mine' | 'free';
  period: 'today' | 'week' | 'month' | 'all';
}

export const defaultFilters: TicketFilters = {
  priority: 'all',
  assignment: 'all',
  period: 'all',
};

export const hasActiveFilters = (f: TicketFilters) =>
  f.priority !== 'all' || f.assignment !== 'all' || f.period !== 'all';

interface SegmentedControlProps {
  options: { id: string; label: string }[];
  value: string;
  onChange: (id: string) => void;
}

const SegmentedControl: React.FC<SegmentedControlProps> = ({ options, value, onChange }) => {
  return (
    <div className="bg-[#EFEFEF] rounded-[13px] p-[3px] flex w-full">
      {options.map((opt) => (
        <button
          key={opt.id}
          onClick={() => onChange(opt.id)}
          className={`flex-1 py-[9px] px-2 rounded-[10px] text-[15px] transition-all duration-200 text-center leading-tight ${
            value === opt.id
              ? 'bg-white shadow-[0_2px_8px_rgba(0,0,0,0.13),0_1px_2px_rgba(0,0,0,0.08)] text-black font-semibold'
              : 'text-[#8E8E93] font-normal hover:text-[#555]'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
};

interface FilterModalProps {
  isOpen: boolean;
  filters: TicketFilters;
  onApply: (filters: TicketFilters) => void;
  onClose: () => void;
}

export const FilterModal: React.FC<FilterModalProps> = ({ isOpen, filters, onApply, onClose }) => {
  const [local, setLocal] = React.useState<TicketFilters>(filters);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) setLocal(filters);
  }, [isOpen, filters]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  const handleReset = () => setLocal(defaultFilters);
  const handleApply = () => { onApply(local); onClose(); };

  const priorityOptions = [
    { id: 'all', label: 'Все' },
    { id: 'high', label: 'Высокий' },
    { id: 'medium', label: 'Средний' },
    { id: 'low', label: 'Низкий' },
  ];

  const assignmentOptions = [
    { id: 'all', label: 'Все' },
    { id: 'mine', label: 'Мне' },
    { id: 'free', label: 'Свободные' },
  ];

  const periodOptions = [
    { id: 'today', label: 'Сегодня' },
    { id: 'week', label: 'Неделя' },
    { id: 'month', label: 'Месяц' },
    { id: 'all', label: 'Всё время' },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            ref={overlayRef}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/30 backdrop-blur-[2px] z-[100]"
            onClick={onClose}
          />

          {/* Modal — bottom sheet on mobile, centered on desktop */}
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ type: 'spring', damping: 28, stiffness: 380, mass: 0.8 }}
            className="fixed z-[101] bottom-0 left-0 right-0 md:inset-0 md:flex md:items-center md:justify-center pointer-events-none"
          >
            <div
              className="bg-white rounded-t-[20px] md:rounded-[20px] w-full md:w-[380px] shadow-[0_20px_60px_rgba(0,0,0,0.2),0_4px_20px_rgba(0,0,0,0.12)] pointer-events-auto overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drag handle (mobile) */}
              <div className="md:hidden flex justify-center pt-3 pb-1">
                <div className="w-9 h-[4px] rounded-full bg-[#D1D1D6]" />
              </div>

              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-[#F2F2F7]">
                <span className="text-[17px] font-semibold text-black tracking-[-0.2px]">Фильтры</span>
                <button
                  onClick={onClose}
                  className="w-7 h-7 rounded-full bg-[#EFEFEF] flex items-center justify-center text-[#8E8E93] hover:bg-[#E5E5EA] transition-colors"
                >
                  <X size={14} strokeWidth={2.5} />
                </button>
              </div>

              {/* Body */}
              <div className="px-5 py-5 space-y-5">
                {/* Priority */}
                <div>
                  <p className="text-[13px] text-[#8E8E93] mb-2.5 tracking-[-0.1px]">Приоритет</p>
                  <SegmentedControl
                    options={priorityOptions}
                    value={local.priority}
                    onChange={(v) => setLocal(p => ({ ...p, priority: v as TicketFilters['priority'] }))}
                  />
                </div>

                {/* Assignment */}
                <div>
                  <p className="text-[13px] text-[#8E8E93] mb-2.5 tracking-[-0.1px]">Назначен</p>
                  <SegmentedControl
                    options={assignmentOptions}
                    value={local.assignment}
                    onChange={(v) => setLocal(p => ({ ...p, assignment: v as TicketFilters['assignment'] }))}
                  />
                </div>

                {/* Period */}
                <div>
                  <p className="text-[13px] text-[#8E8E93] mb-2.5 tracking-[-0.1px]">Период</p>
                  <SegmentedControl
                    options={periodOptions}
                    value={local.period}
                    onChange={(v) => setLocal(p => ({ ...p, period: v as TicketFilters['period'] }))}
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center gap-3 px-5 pb-[var(--mobile-sheet-safe-bottom)] md:pb-5 pt-1">
                <button
                  onClick={handleReset}
                  className="text-[#007AFF] text-[16px] font-medium px-4 py-3 hover:opacity-70 transition-opacity flex-shrink-0"
                >
                  Сбросить
                </button>
                <button
                  onClick={handleApply}
                  className="flex-1 bg-[#007AFF] hover:bg-[#0066DD] active:scale-[0.98] text-white text-[16px] font-semibold py-[13px] rounded-[13px] transition-all duration-150"
                >
                  Применить
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
